import { FC, useEffect, useRef, useState } from 'react';
import {
  Trigger,
  Portal,
  Content,
  Close,
  Arrow,
  Root,
} from '@radix-ui/react-popover';
import styled from 'styled-components';
import tw from 'twin.macro';
import TooltipInfo from './TooltipInfo';
import Button from './Button';
import { ButtonColor, ButtonSize, Icons } from '../enums';
import { ColumnType } from '../types/dataTypes';
import strings from '../language/Swedish';
import Checkbox from './Checkbox';
import { getSelectedColumnOptions, saveSelectedColumnOptions } from './table/helpers';
import { useProjectContext } from '@/context/ProjectContext';

const StyledContent = styled(Content)`
  ${tw`rounded-xl z-50 mr-4 bg-white border shadow-xl backdrop-blur border-white`};

  @media only screen and (max-width: 640px) {
    max-height: 60vh;
    display: flex;
    flex-direction: column;
  }
`;

const TopContainer = styled.div`
  ${tw`flex items-center justify-center gap-1 rounded-t-xl p-1 bg-gradient-to-b from-white to-white/0 border-b border-slate-200`};
`;

const Header = styled.p`
  ${tw`flex-1 text-sm mx-2 my-0 text-slate-700`};
`;

const CheckboxContainer = styled.div`
  ${tw`py-2 px-4 gap-2 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 `};

  @media only screen and (max-width: 640px) {
    max-height: 50vh;
    overflow-y: scroll;
    overscroll-behavior: contain;
  }
`;

const StyledCheckbox = styled(Checkbox)`
  ${tw`py-1`};
`;

const StyledArrow = styled(Arrow)`
  transform: translateX(-14px);
`;

interface Props {
  columns: ColumnType[];
  color?: ButtonColor;
  toggleColumn: (id: string) => void;
  columnOption?: string | number;
}
const ColumnChoiceDialog: FC<Props> = ({
  columns,
  color = ButtonColor.border,
  toggleColumn,
  columnOption
}) => {
  const { setProject } = useProjectContext();
  const [open, setOpen] = useState(false);
  const [localColumns, setLocalColumns] = useState<ColumnType[]>(columns);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 640);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (columnOption === 'ownChoice') {
      const savedColumns = getSelectedColumnOptions();
      setLocalColumns(savedColumns);
    } else {
      setLocalColumns(columns);
    }
  }, [columnOption, columns]);

  useEffect(() => {
    if (!open) return;
    //To enable scroll behind modal, add timeout so that ref will not be null
    const timeout = setTimeout(() => {
      const el = contentRef.current;
      if (!el) return;
  
      const scrollNode = document.querySelector('main');
  
      const handleWheel = (e: WheelEvent) => {
        if ((e.target as HTMLElement).closest("button, input, select, textarea")) {
          return;
        }
        if (!scrollNode) return;

        e.preventDefault();
  
        scrollNode.scrollBy({
          top: e.deltaY,
          left: e.deltaX,
          behavior: "auto",
        });
      };
  
      el.addEventListener("wheel", handleWheel, { passive: false });
      return () => el.removeEventListener("wheel", handleWheel);
    }, 0);
    return () => clearTimeout(timeout);
  }, [open]);

  useEffect(() => {
    const displayColumns = getColumnsToDisplay();
    sortColumns(displayColumns);
  }, [localColumns, columns, columnOption]);

  function getColumnsToDisplay(): ColumnType[] {
    return columnOption === "ownChoice" ? localColumns : columns;
  }

  function sortColumns(columns: ColumnType[]): ColumnType[] {
    if (columnOption === "ownChoice") {
      const ownChoiceColumns = localColumns;
      return ownChoiceColumns;
    } else {
      const sorted = columns.sort((a, b) => a.label.toLocaleLowerCase().replace(".", "").localeCompare(b.label.toLocaleLowerCase().replace(".", ""), 'sv', { sensitivity: 'base' }));
      return sorted;
    }
  }

  const handleToggleColumn = (id: string) => {
		if (columnOption === 'ownChoice') {
      const updatedColumns = localColumns.map((column) => {
        if (column.id === id) {
          return {
            ...column,
            isVisible: !column.isVisible
          }
        } else {
          return column
        }
      });

      setProject((prevState) => ({
        ...prevState,
        soldTableColumns: updatedColumns,
        soldTableReportColumns: updatedColumns,
      }));
      
      setLocalColumns(updatedColumns);
      saveSelectedColumnOptions(updatedColumns);
		} else {
			toggleColumn(id);
		}
	};

  return (
    <Root modal={false} onOpenChange={setOpen} open={open}>
      <Trigger asChild>
        <div>
          <TooltipInfo content={strings.table.columnButtonInfo}>
            <div>
              <Button
                icon={Icons.columns}
                color={color}
                size={ButtonSize.medium}
                name={'select columns'}
              />
            </div>
          </TooltipInfo>
        </div>
      </Trigger>
      <Portal>
        <StyledContent
          sideOffset={5}
          ref={contentRef}
          collisionPadding={isMobile ? { top: 80, right: 10, bottom: 10, left: 10 } : 10}
          avoidCollisions={true}
          side={isMobile ? "bottom" : undefined}
          align="end"
          sticky="always"
        >
          <TopContainer>
            <Header>{strings.table.columnHeader}</Header>
            <Close asChild aria-label="Close">
              <div>
                <Button
                  color={ButtonColor.transparent}
                  size={ButtonSize.small}
                  icon={Icons.cross}
                  aria-label="Close"
                />
              </div>
            </Close>
          </TopContainer>
          <CheckboxContainer>
            {sortColumns(getColumnsToDisplay()).map((column) => (
              <StyledCheckbox
                id={column.id}
                key={column.id}
                label={column.label}
                isChecked={column.isVisible}
                onToggle={() => {
                  handleToggleColumn(column.id);
                }}
              />
            ))}
          </CheckboxContainer>
          <StyledArrow className="fill-white" />
        </StyledContent>
      </Portal>
    </Root>
  );
}

export default ColumnChoiceDialog;
